import { useQuery } from '@tanstack/react-query';
import { API } from 'api';

import { useSearchDrawerTabs } from 'components/IncidentsMap/SearchDrawer/hooks/useSearchDrawerTabs';
import { getMapBounds } from 'components/IncidentsMap/SearchDrawer/useVisibleGeoEvents';
import { Report } from 'features/reports/report.types';
import { useMapState, UseMapState } from 'state/useMapState';
import { LngLat } from 'shared/map-exports';
import { useSearchDrawerState } from 'state/useSearchDrawerState';

type UseModerationReportProps = {
  forceEnabled?: boolean;
};

type UseModerationReports = {
  reports: Report[];
  reportsCount: number;
  reportsError: Error | null;
  reportsIsError: boolean;
  reportsIsLoading: boolean;
};

const filterVisibleReports = (
  incidentMapState: UseMapState['incidentMapState'],
  reports: Report[],
): Report[] => {
  const { bounds } = incidentMapState;
  if (!bounds) return [];

  const visibleBounds = getMapBounds(bounds);

  return reports.filter(
    (report) =>
      report.media?.[0]?.lat != null &&
      report.media?.[0]?.lng != null &&
      visibleBounds.contains(
        new LngLat(report.media[0].lng, report.media[0].lat),
      ),
  );
};

const fetchUnmoderatedReports = async (): Promise<Report[]> => {
  const resp = await API.get(`reports/?status=llm_reviewed&has_lat_lng=true`);
  return resp.data;
};

const sorter = (a: Report, b: Report): number => {
  // To make life easier for reporters, we move user submitted photos to the top, then the Ring photos with
  // detections, and then the rest of the Ring photos. Each subgroup is sorted by creation time, newest first

  // Only one of them is user submitted
  if (a.ringDeviceId == null && b.ringDeviceId != null) return -1;
  if (a.ringDeviceId != null && b.ringDeviceId == null) return 1;

  const aTimestamp = new Date(a.dateCreated).getTime();
  const bTimestamp = new Date(b.dateCreated).getTime();

  // They are both user submitted
  if (a.ringDeviceId == null) {
    return bTimestamp - aTimestamp;
  }

  // don't have a 100% way of checking for this at this time, but that's fine
  const aHasDetections = a.message.startsWith('Ring camera, detected:');
  const bHasDetections = b.message.startsWith('Ring camera, detected:');

  // Only one of them has detections
  if (aHasDetections && !bHasDetections) return -1;
  if (!aHasDetections && bHasDetections) return 1;

  // They are both ring photos and they both either have detections or don't
  return bTimestamp - aTimestamp;
};

export const useModerationReports = (
  props: UseModerationReportProps = {},
): UseModerationReports => {
  const { forceEnabled = false } = props;
  const showPhotosTab = useSearchDrawerTabs();
  const { open: isSearchDrawerOpen } = useSearchDrawerState();
  const { incidentMapState } = useMapState();

  const { data, error, isError, isLoading } = useQuery({
    queryKey: ['moderationReports'],
    queryFn: fetchUnmoderatedReports,
    enabled: (showPhotosTab && isSearchDrawerOpen) || forceEnabled,
    refetchInterval: 30_000,
  });

  const visibleReports = filterVisibleReports(incidentMapState, data ?? []);
  visibleReports.sort(sorter);

  return {
    reports: visibleReports,
    reportsCount: visibleReports.length,
    reportsError: error,
    reportsIsError: isError,
    reportsIsLoading: isLoading,
  };
};
